import React from 'react';

import AutoEnrolIcon from 'icons/double-arrow-rotate.svg';
import IHascoBrandIcon from 'icons/ihasco-brand.svg';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import TableCell from 'components/application/TableCell';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import CourseTrainingStatusIndicator from 'components/courses/CourseTrainingStatusIndicator';

import { formatDuration } from 'components/helpers/dates';
import {
  getELearningCourseId,
  isCourseEligibleForAutoEnrolment,
} from 'components/helpers/resources/courses';

export default function CourseRow({ course, onRowClick, originalCourse }) {
  const currentActor = useCurrentActor();

  const { isAutoEnrolmentIncludingMissingTraining } =
    useTrainingRegisterResources();

  const { t: i18nTCommon } = useTranslation('common');

  const {
    expires,
    learningMethod,
    name,
    provider,
    renewalFrequency,
    renewalInterval,
  } = course.attributes;

  const {
    personnelCount,
    requirementPriorityCount,
    roleCount,
    trainingStatus,
  } = course.meta;

  const courseELearningCourseId =
    currentActor.isAllowedFeature('e_learning') && getELearningCourseId(course);

  const originalCourseELearningCourseId =
    currentActor.isAllowedFeature('e_learning') &&
    getELearningCourseId(originalCourse);

  return (
    <tr
      className='handshq__tr--clickable tw-h-[49px] hover:tw-bg-grey-025'
      onClick={() => onRowClick(course, originalCourse)}
    >
      <TableCell justifyContent='center'>
        <CourseTrainingStatusIndicator status={trainingStatus} />
      </TableCell>
      <TableCell>
        <>
          <div className='truncated-text-container truncated-text-container--auto tooltip-parent tw-h-5'>
            <span className='tw-text-m tw-font-medium tw-text-grey-900'>
              {name}
            </span>
            {name.length > 60 && (
              <Tooltip placement='top' tooltip={name} trigger='hover' />
            )}
          </div>
          {currentActor.isAllowedFeature('e_learning') &&
            !courseELearningCourseId &&
            !!originalCourseELearningCourseId && (
              <div className='tw-ml-2 tw-flex-none'>
                <div className='tw-flex tw-h-6 tw-items-center tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-2 tw-py-[3px]'>
                  <IHascoBrandIcon className='tw-mr-1' height={12} width={56} />
                  <span className='tw-text-s tw-font-normal tw-tracking-wide'>
                    available
                  </span>
                </div>
              </div>
            )}
        </>
      </TableCell>
      <TableCell>
        {(
          isCourseEligibleForAutoEnrolment({
            currentActor,
            isAutoEnrolmentIncludingMissingTraining,
            course,
          })
        ) ?
          <div className='truncated-text-container'>
            <span className='tw-text-m'>
              {i18nTCommon('courses.learningMethods.eLearning')}
            </span>
            <div className='tw-flex tw-items-center tw-text-s tw-text-grey-500'>
              <AutoEnrolIcon className='[&_path]:tw-fill-grey-500' />
              <span>{i18nTCommon('courses.autoEnrols')}</span>
            </div>
          </div>
        : <span className='truncated-text-container tw-text-m'>
            {learningMethod &&
              i18nTCommon(
                `courses.learningMethods.${voca.camelCase(learningMethod)}`,
              )}
          </span>
        }
      </TableCell>
      <TableCell>
        <span className='truncated-text-container tw-text-m'>{provider}</span>
      </TableCell>
      <TableCell>
        <span className='tw-text-m'>
          {expires ?
            renewalFrequency && renewalInterval ?
              formatDuration({
                durationParts: {
                  [pluralize(renewalInterval)]: renewalFrequency,
                },
              })
            : null
          : i18nTCommon('shared.doesNotExpire')}
        </span>
      </TableCell>
      <TableCell justifyContent='center'>
        <div className='tw-text-m'>{roleCount}</div>
      </TableCell>
      <TableCell justifyContent='center'>
        <div className='tw-text-m'>{personnelCount}</div>
      </TableCell>
      <TableCell justifyContent='center'>
        <div className='tw-text-m'>{requirementPriorityCount}</div>
      </TableCell>
    </tr>
  );
}

CourseRow.propTypes = {
  course: PropTypes.object.isRequired,
  onRowClick: PropTypes.func.isRequired,
  originalCourse: PropTypes.object,
};
